import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { format } from 'date-fns'

import { listOrganizationMarginReports } from '@enechain/eposition-proto/bffportal/v1/report_service-ReportService_connectquery'
import { ListOrganizationMarginReportsRequestSchema } from '@enechain/eposition-proto/bffportal/v1/report_service_pb'
import { convertGoogleDateModel } from '@enechain/eposition-ui'

import {
  type MarginReportEntryModel,
  convertMarginReportEntryModels,
} from '~/pages/MarginReport/models/marginReportEntry'

function calcDateRange(period: 30 | 90): {
  fromDate: ReturnType<typeof convertGoogleDateModel>
  toDate: ReturnType<typeof convertGoogleDateModel>
} {
  const today = new Date()
  const from = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - period,
  )

  return {
    fromDate: convertGoogleDateModel(from),
    toDate: convertGoogleDateModel(today),
  }
}

function trimEntries(
  entries: MarginReportEntryModel[],
  period: 30 | 90,
): MarginReportEntryModel[] {
  const sorted = [...entries].sort((a, b) => a.date.localeCompare(b.date))
  if (sorted.length === 0) {
    return sorted
  }

  const lastEntry = sorted[sorted.length - 1]
  const todayStr = format(new Date(), 'yyyy-MM-dd')

  if (lastEntry.date !== todayStr) {
    return sorted
  }

  if (lastEntry.deposits === null) {
    return sorted.slice(0, sorted.length - 1)
  }

  return sorted.slice(sorted.length - period)
}

type Result = {
  entries: MarginReportEntryModel[]
  isLoading: boolean
  error: Error | null
}

export function useListOrganizationMarginReports({
  period,
}: {
  period: 30 | 90
}): Result {
  const { fromDate, toDate } = calcDateRange(period)

  const request = create(ListOrganizationMarginReportsRequestSchema, {
    fromDate,
    toDate,
  })

  const { data, isLoading, error } = useQuery(
    listOrganizationMarginReports,
    request,
    { throwOnError: false },
  )

  const entries = useMemo(
    () =>
      trimEntries(
        data != null ? convertMarginReportEntryModels(data.entries) : [],
        period,
      ),
    [data, period],
  )

  return {
    entries,
    isLoading,
    error: error ?? null,
  }
}
